import { runInAction } from 'mobx';
import DesignModel from './DesignModel';
import AbstractSessionModel from '../../../models/AbstractSessionModel';
import Observable from '../../../decorators/Observable';
import StoreFieldInSession from '../../../decorators/StoreFieldInSession';
import Action from '../../../decorators/Action';

class ColorThemeStateMachine extends AbstractSessionModel {
  @StoreFieldInSession()
  @Observable()
  public primaryColor: string = '#3498DB';

  @StoreFieldInSession()
  @Observable()
  public secondaryColor: string = '#E74C3C';

  @StoreFieldInSession()
  @Observable()
  public colorSchema: any = null;

  public stateMachine = {
    currentState: 'idle',
    states: {
      idle: {
        onEnter: this.idle.bind(this),
        transitions: {
          selectLogo: () => 'logoSelected',
          uploadLogo: () => 'logoSelected',
          changedPrimaryColorByUser: () => 'primaryColorChanged',
          changedSecondaryColorByUser: () => 'secondaryColorChanged',
          setInitialColorSchema: () => 'setInitialColorSchema'
        }
      },
      setInitialColorSchema: {
        onEnter: this.setInitialColorSchema.bind(this),
        transitions: {
          selectLogo: () => 'logoSelected',
          uploadLogo: () => 'logoSelected',
          changedPrimaryColorByUser: () => 'primaryColorChanged',
          changedSecondaryColorByUser: () => 'secondaryColorChanged'
        }
      },
      logoSelected: {
        onEnter: this.logoSelected.bind(this),
        transitions: {
          changedSecondaryColorByUser: () => 'secondaryColorChanged',
          changedPrimaryColorByUser: () => 'primaryColorChanged',
          selectLogo: () => 'logoSelected',
          uploadLogo: () => 'logoSelected'
        }
      },
      primaryColorChanged: {
        onEnter: this.primaryColorChanged.bind(this),
        transitions: {
          selectLogo: () => 'logoSelectedAfterUserChangedColors',
          uploadLogo: () => 'logoSelectedAfterUserChangedColors',
          changedPrimaryColorByUser: () => 'primaryColorChanged',
          changedSecondaryColorByUser: () => 'secondaryColorChanged'
        }
      },
      primaryColorChangedAfterSecondaryChanged: {
        onEnter: this.primaryColorChangedAfterSecondaryChanged.bind(this),
        transitions: {
          selectLogo: () => 'logoSelectedAfterUserChangedColors',
          uploadLogo: () => 'logoSelectedAfterUserChangedColors',
          changedPrimaryColorByUser: () =>
            'primaryColorChangedAfterSecondaryChanged',
          changedSecondaryColorByUser: () => 'secondaryColorChanged'
        }
      },
      secondaryColorChanged: {
        onEnter: this.secondaryColorChanged.bind(this),
        transitions: {
          selectLogo: () => 'logoSelectedAfterUserChangedColors',
          uploadLogo: () => 'logoSelectedAfterUserChangedColors',
          changedPrimaryColorByUser: () =>
            'primaryColorChangedAfterSecondaryChanged',
          changedSecondaryColorByUser: () => 'secondaryColorChanged'
        }
      },
      logoSelectedAfterUserChangedColors: {
        onEnter: this.logoSelectedAfterUserChangedColors.bind(this),
        transitions: {
          keepColors: () => 'colorsKept',
          recalculateColors: () => 'colorsRecalculation'
        }
      },
      colorsKept: {
        onEnter: this.colorsKept.bind(this),
        transitions: {
          selectLogo: () => 'logoSelected',
          uploadLogo: () => 'logoSelected',
          changedPrimaryColorByUser: () => 'primaryColorChanged',
          changedSecondaryColorByUser: () => 'secondaryColorChanged'
        }
      },
      colorsRecalculation: {
        onEnter: this.colorsRecalculation.bind(this),
        transitions: {
          selectLogo: () => 'logoSelected',
          uploadLogo: () => 'logoSelected',
          changedPrimaryColorByUser: () => 'primaryColorChanged',
          changedSecondaryColorByUser: () => 'secondaryColorChanged'
        }
      }
    }
  };

  constructor() {
    super();
    super.initFields();
  }

  private setInitialColorSchema(params) {
    const { colorSchema } = params;
    this.colorSchema = colorSchema;

    // change the primary color
    this.primaryColor = colorSchema.primary.main;

    // change the secondary color
    this.secondaryColor = colorSchema.secondary.main;
  }

  private idle() {}

  private logoSelected(params) {
    runInAction(() => {
      const { colorSchema } = params;
      this.colorSchema = colorSchema;

      // change the primary color
      this.primaryColor = colorSchema.primary.main;

      // change the secondary color
      this.secondaryColor = colorSchema.secondary.main;
    });
  }

  private primaryColorChanged(params) {
    runInAction(() => {
      const { primaryColor, newColorSchema } = params;
      this.primaryColor = primaryColor;

      // recalculate colors
      if (newColorSchema) {
        this.colorSchema = newColorSchema;

        // change the secondary color
        this.secondaryColor = newColorSchema.secondary.main;
      }
    });
  }

  private primaryColorChangedAfterSecondaryChanged(params) {
    // recaulculate colors without the secondary color
    runInAction(() => {
      const { primaryColor, newColorSchema } = params;
      this.primaryColor = primaryColor;

      if (newColorSchema) {
        this.colorSchema = newColorSchema;
        this.colorSchema.secondary.main = this.secondaryColor;
      }
    });
  }

  private secondaryColorChanged(params) {
    runInAction(() => {
      // do not change the primary color here

      const { secondaryColor } = params;
      this.secondaryColor = secondaryColor;

      // update the color schema
      this.colorSchema.secondary.main = secondaryColor;
    });
  }

  private logoSelectedAfterUserChangedColors(params) {
    // - show modal here
    runInAction(() => {
      DesignModel.openColorThemeConfirmationModal();
    });
  }

  private colorsKept(params) {
    // do not change the primary color, leave it as it is
    runInAction(() => {});
  }

  private colorsRecalculation(params) {
    // change the primary color, update the color schema
    runInAction(() => {
      const { newColorSchema } = params;

      this.colorSchema = newColorSchema;

      this.primaryColor = newColorSchema.primary.main;
      this.secondaryColor = newColorSchema.secondary.main;
    });
  }

  @Action()
  public transition(event, params) {
    const nextState =
      this.stateMachine.states[this.stateMachine.currentState].transitions[
        event
      ];
    if (nextState) {
      this.stateMachine.currentState = nextState();
      this.stateMachine.states[this.stateMachine.currentState].onEnter(params);
    } else {
      //console.warn(
      //  `Invalid transition from ${this.stateMachine.currentState} on event ${event}`
      //);
    }
  }

  public getSessionKey() {
    return 'ColorThemeStateMachine';
  }
}

export default ColorThemeStateMachine;
